import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Typography, withStyles } from '@material-ui/core'
import styles from './styles'
import { CoreButton, CoreIconButton } from 'src/components/core'
import { Icon } from '@iconify/react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  messageBoxShowAction,
  messageBoxHideAction,
} from 'src/reducers/message-box/action'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import LandingPageSettings from 'src/components/landing-page-settings'
import {
  courseGetAction,
  getCourseLandingPageAction,
  patchCourseLandingPageAction,
} from 'src/reducers/course/action'
import { useParams } from 'react-router-dom'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import {
  getCollectionLandingPageAction,
  patchCollectionLandingPageAction,
} from 'src/reducers/collection/action'
import { editorEnum } from 'src/model/editor/editorEnum'
import { setPageAccent } from 'src/components/editor/components/custom-plugins/simple-box/catalog'
import {
  getChangedFields,
  mapSettingsToPatchFormat,
} from 'src/utils/patchHelper'
import { useSnackbar } from 'notistack'
import {
  getPortalPageEditing,
  setPortalPageEditing,
  showHeaderTitle,
  subscribePortalPageEditing,
} from 'src/utils/portal-page-editing'
import { portalPageUpdateService } from 'src/api/collection'
import { slugForRename } from 'src/utils/portal-page-slug'
import { loadWorkspaceLogo, resolveFont } from 'src/utils/workspace-logo'
import usePortal from 'src/hooks/usePortal'
import { track } from '@amplitude/analytics-browser'

const COPY_FLAG_KEY = 'mcg-show-paste-button'
const COPY_SOURCE_TYPE_KEY = 'mcg-copy-source-type'

const resolvePasteContentType = () => {
  const sourceType = localStorage.getItem(COPY_SOURCE_TYPE_KEY)
  if (['block', 'page', 'landing_page'].includes(sourceType)) {
    return sourceType
  }
  return 'unknown'
}

/** The editor's content, as its converters write it (getData() is post-processed by a plugin). */
const editorSnapshot = editor => {
  try {
    return editor.data.stringify(editor.model.document.getRoot())
  } catch (error) {
    return null
  }
}

/**
 * Tracks whether the user changed the editor content. Plugins rewrite the
 * content while it loads (block markup, fetched data), so the baseline follows
 * the editor until the user first interacts with the page outside this header;
 * after that, any difference from the baseline is an unsaved change.
 * `markSaved` resets the baseline after a save.
 */
const useEditorChanges = headerRef => {
  const baselineRef = useRef(null)
  const touchedRef = useRef(false)

  useEffect(() => {
    let editor = null

    const onChange = () => {
      if (!touchedRef.current && editor)
        baselineRef.current = editorSnapshot(editor)
    }
    // The editor can be created (or recreated) after this header mounts.
    const attach = () => {
      if (!window.editor || window.editor === editor) return
      if (editor) editor.model.document.off('change:data', onChange)
      editor = window.editor
      editor.model.document.on('change:data', onChange)
      if (!touchedRef.current) baselineRef.current = editorSnapshot(editor)
    }
    attach()
    const timer = setInterval(attach, 500)

    const onInteract = event => {
      if (headerRef.current?.contains(event.target)) return
      touchedRef.current = true
    }
    const events = ['keydown', 'mousedown', 'paste', 'drop']
    events.forEach(name => document.addEventListener(name, onInteract, true))

    return () => {
      clearInterval(timer)
      if (editor) editor.model.document.off('change:data', onChange)
      events.forEach(name =>
        document.removeEventListener(name, onInteract, true),
      )
    }
  }, [headerRef])

  const hasChanges = () => {
    if (!window.editor || !touchedRef.current) return false
    const current = editorSnapshot(window.editor)
    const changed = current !== null && current !== baselineRef.current
    // TODO(debug): temporary, to find what differs when nothing was edited.
    if (changed) {
      const before = baselineRef.current || ''
      let at = 0
      while (at < before.length && before[at] === current[at]) at += 1
      // eslint-disable-next-line no-console
      console.warn('[unsaved-changes] first difference at', at, {
        lengths: [before.length, current.length],
        before: before.slice(Math.max(0, at - 120), at + 200),
        after: current.slice(Math.max(0, at - 120), at + 200),
      })
    }
    return changed
  }

  const markSaved = () => {
    touchedRef.current = false
    if (window.editor) baselineRef.current = editorSnapshot(window.editor)
  }

  return { hasChanges, markSaved }
}

/**
 * The open portal page's name in the editor header, with a pencil to rename
 * it in place: Enter or leaving the field saves, Escape cancels.
 */
const PortalPageName = ({ name, onRename }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const [saving, setSaving] = useState(false)

  useEffect(() => setDraft(name), [name])

  const finish = () => {
    const next = draft.trim()
    if (!next || next === name) {
      setDraft(name)
      setEditing(false)
      return
    }
    setSaving(true)
    onRename(next)
      .then(saved => {
        if (!saved) setDraft(name)
        setEditing(false)
      })
      .finally(() => setSaving(false))
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        maxLength={100}
        disabled={saving}
        onChange={event => setDraft(event.target.value)}
        onBlur={finish}
        onKeyDown={event => {
          if (event.key === 'Enter') event.target.blur()
          if (event.key === 'Escape') {
            setDraft(name)
            setEditing(false)
          }
        }}
        style={{
          width: Math.max(120, draft.length * 9),
          padding: '3px 8px',
          border: '1px solid #015FFB',
          borderRadius: 6,
          outline: 'none',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 600,
          color: '#002E47',
        }}
      />
    )
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#002E47' }}>
        {name}
      </span>
      <button
        type="button"
        title="Rename page"
        onClick={() => setEditing(true)}
        style={{
          display: 'inline-flex',
          padding: 3,
          border: 0,
          borderRadius: 6,
          background: 'transparent',
          cursor: 'pointer',
          color: '#637381',
        }}
      >
        <Icon icon="tabler:pencil" width={14} height={14} />
      </button>
    </span>
  )
}

const CoverHeader = props => {
  const { classes, url, course } = props
  const [copied, setCopied] = useState(false)
  const [clipboardHasEditorContent, setClipboardHasEditorContent] = useState(
    false,
  )
  const { enqueueSnackbar } = useSnackbar()
  // Use a ref to store initial content
  const initialContentRef = useRef('')
  // Use a ref for latest saved content as well
  const savedContentRef = useRef('')
  const headerRef = useRef(null)
  const editorChanges = useEditorChanges(headerRef)
  // The editor header shows the workspace logo when the page has none.
  useEffect(() => {
    loadWorkspaceLogo()
  }, [])
  const { id, page, subPage } = useParams()
  const history = useHistory()
  const dispatch = useDispatch()
  const pageUrl = window.location.pathname.split('/')[1]
  // A portal page open in this editor (see utils/portal-page-editing).
  const [portalPage, setPortalPage] = useState(getPortalPageEditing)
  useEffect(
    () =>
      subscribePortalPageEditing(page => {
        if (page) setPortalPage(page)
      }),
    [],
  )
  const showcase = useSelector(state => state.collection.collection)
  const portalLinks = usePortal(portalPage ? showcase?.shopKey : null)
  const portalHost = (portalLinks.home || '').replace(/^https?:\/\//, '')
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  // Create a safe version of landingPageData with fallback to empty object
  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings || {} : collectionSettings || {}

  var landingPageSection = landingPageData?.sections.find(
    x => x.type === editorEnum.headerType,
  )
  var landingPageSectionMain = landingPageData?.sections.find(
    x => x.type === editorEnum.mainType,
  )
  // Store initial content on component mount
  useEffect(() => {
    // Set a delay to ensure CKEditor is fully loaded
    const timer = setTimeout(() => {
      if (window.editor) {
        const content = window.editor.getData().trim()
        initialContentRef.current = content
        savedContentRef.current = content
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [])

  const checkClipboard = useCallback(() => {
    navigator.clipboard
      .readText()
      .then(text => {
        const hasEditorContent = !!text && text.includes('class="simple-box')
        if (!hasEditorContent) {
          localStorage.removeItem(COPY_FLAG_KEY)
          localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
        }
        setClipboardHasEditorContent(hasEditorContent)
      })
      .catch(() => {
        localStorage.removeItem(COPY_FLAG_KEY)
        localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
        setClipboardHasEditorContent(false)
      })
  }, [])

  useEffect(() => {
    checkClipboard()
    const onCopy = () => setTimeout(checkClipboard, 100)
    window.addEventListener('focus', checkClipboard)
    document.addEventListener('copy', onCopy)
    window.addEventListener('mcg-editor-copy', checkClipboard)
    return () => {
      window.removeEventListener('focus', checkClipboard)
      document.removeEventListener('copy', onCopy)
      window.removeEventListener('mcg-editor-copy', checkClipboard)
    }
  }, [checkClipboard])

  const handleEditorCopy = useCallback(async () => {
    if (window.editor) {
      const data = window.editor.getData()
      await navigator.clipboard.writeText(data)
      localStorage.setItem(COPY_FLAG_KEY, '1')
      localStorage.setItem(COPY_SOURCE_TYPE_KEY, 'landing_page')
      setClipboardHasEditorContent(true)
      track('landing_page_copied', {
        copy_content: 'landing_page',
        page_context: 'landing_page_editor',
      })
      enqueueSnackbar('Page copied to clipboard', { variant: 'success' })
    }
  }, [enqueueSnackbar])

  const handleEditorPaste = useCallback(() => {
    if (!window.editor) return
    navigator.clipboard.readText().then(data => {
      if (!data || !data.includes('class="simple-box')) return
      const viewFragment = window.editor.data.processor.toView(data)
      const modelFragment = window.editor.data.toModel(viewFragment)
      window.editor.model.change(writer => {
        const root = window.editor.model.document.getRoot()
        const firstChild = root.getChild(0)
        if (firstChild) {
          writer.insert(modelFragment, root, firstChild.index + 1)
        } else {
          writer.insert(modelFragment, root, 0)
        }
      })
      track('pasted_to_landing_page', {
        paste_content: resolvePasteContentType(),
        page_context: 'landing_page_editor',
      })
      localStorage.removeItem(COPY_FLAG_KEY)
      localStorage.removeItem(COPY_SOURCE_TYPE_KEY)
      setClipboardHasEditorContent(false)
      enqueueSnackbar('Copied content pasted below header', {
        variant: 'success',
      })
    })
  }, [enqueueSnackbar])

  const handleClick = () => {
    const currentData = window.editor
      .getData()
      .replace(/<p class="empty-dom">&nbsp;<\/p>/g, '')
      .trim()
    const patchAction =
      pageUrl === 'mini-course'
        ? patchCourseLandingPageAction
        : patchCollectionLandingPageAction

    var landingPageSection = landingPageData?.sections.find(
      x => x.type === editorEnum.headerType,
    )
    if (pageUrl === 'collection') {
      dispatch(getCollectionLandingPageAction(id))
    } else if (pageUrl === 'mini-course') {
      dispatch(getCourseLandingPageAction(id))
    }
    dispatch(
      patchAction(id, [
        {
          path: 'sections',
          value: [
            {
              type: editorEnum.headerType,
              content: {
                alignment: landingPageSection.content.aligment,
                image: landingPageSection.content.image,
              },
            },
            {
              type: editorEnum.mainType,
              content: {
                html: currentData,
              },
            },
          ],
        },
      ]),
    ).then(item => {
      if (item) {
        // Use setTimeout to delay navigation by 2 seconds
        setTimeout(() => {
          navigateAway()
        }, 500)
      }
    })

    // Update saved content reference after saving
    savedContentRef.current = currentData
    editorChanges.markSaved()
  }

  /** Renames the open portal page; its URL follows the name as in Navigation (slugForRename). */
  const renamePortalPage = name =>
    slugForRename(
      { type: portalPage.type, id: portalPage.pageId, slug: portalPage.slug },
      name,
    )
      .then(slug => portalPageUpdateService(portalPage.pageId, { name, slug }))
      .then(response => {
        const saved = response?.data?.pages?.find(
          page => page.id === portalPage.pageId,
        )
        const next = {
          ...portalPage,
          name: saved?.name || name,
          slug: saved?.slug ?? portalPage.slug,
          path: saved?.path || portalPage.path,
        }
        setPortalPage(next)
        setPortalPageEditing(next)
        showHeaderTitle(next.name)
        enqueueSnackbar('Saved', { variant: 'success' })
        return true
      })
      .catch(error => {
        enqueueSnackbar(
          error?.response?.data?.operationMessage ||
            'Could not rename the page.',
          { variant: 'error' },
        )
        return false
      })

  let latestSettings = null
  // Store the initial settings when modal opens
  const initialSettingsRef = useRef(null)

  const handleSettings = () => {
    // Store initial settings when opening the modal
    initialSettingsRef.current = mapSettingsToPatchFormat(landingPageData)
    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 16px; font-weight: 400; line-height: 150%;">
            <Typography>Landing page settings</Typography>
          </div>`,
          type: MessageBoxType.INFO,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction())
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Save',
              ButtonActionType.YES,
              () => {
                if (latestSettings) {
                  // Get the current landing page type
                  const currentLandingPageType =
                    landingPageSection?.content?.type || 1

                  // Map latest settings to patch format
                  const mappedLatestSettings = mapSettingsToPatchFormat(
                    latestSettings,
                  )

                  // Get only changed fields
                  const changedFields = getChangedFields(
                    initialSettingsRef.current,
                    mappedLatestSettings,
                  )

                  // Only proceed if there are actual changes
                  if (changedFields.length === 0) {
                    dispatch(messageBoxHideAction())
                    return
                  }

                  // Create a patch request with only changed settings
                  const patchAction =
                    pageUrl === 'mini-course'
                      ? patchCourseLandingPageAction
                      : patchCollectionLandingPageAction

                  dispatch(patchAction(id, changedFields))

                  // Update font styles based on landing page type
                  const fontClasses = {
                    1: [
                      'course-container-header-text',
                      'start-button-text-header',
                      'start-button-text',
                    ],
                    2: [
                      'hero-title',
                      'hero-description',
                      'hero-start-course-btn',
                      'hero-see-more-btn',
                    ],
                    3: [
                      'promo-title',
                      'promo-description',
                      'promo-get-started-btn',
                      'promo-feature-text',
                    ],
                  }

                  // Apply font family to all relevant elements
                  const classesToUpdate =
                    fontClasses[currentLandingPageType] || fontClasses[1]
                  classesToUpdate.forEach(className => {
                    Array.from(
                      document.getElementsByClassName(className),
                    ).forEach(element => {
                      // Not chosen (Inter): the workspace font, as learners see it.
                      element.style.fontFamily = resolveFont(latestSettings.font)

                      // Keep feature text color always #002E47
                      if (className === 'promo-feature-text') {
                        element.style.color = '#002E47'
                      }
                    })
                  })

                  // Update primary color based on landing page type
                  if (currentLandingPageType === 1) {
                    // Type 1: Default
                    Array.from(
                      document.getElementsByClassName(
                        'start-button-text-header',
                      ),
                    ).forEach(element => {
                      element.style.color = latestSettings.primaryColor
                    })
                    Array.from(
                      document.getElementsByClassName('start-button'),
                    ).forEach(element => {
                      element.style.background = latestSettings.primaryColor
                    })
                  } else if (currentLandingPageType === 2) {
                    // Type 2: Hero
                    Array.from(
                      document.getElementsByClassName('hero-start-course-btn'),
                    ).forEach(element => {
                      element.style.backgroundColor =
                        latestSettings.primaryColor
                    })
                    Array.from(
                      document.getElementsByClassName('hero-see-more-btn'),
                    ).forEach(element => {
                      element.style.color = latestSettings.primaryColor
                      element.style.borderColor = latestSettings.primaryColor
                    })
                  } else if (currentLandingPageType === 3) {
                    // Type 3: Promo
                    Array.from(
                      document.getElementsByClassName('promo-get-started-btn'),
                    ).forEach(element => {
                      element.style.backgroundColor =
                        latestSettings.primaryColor
                    })
                  }

                  // The empty catalog's icon and the "Your learning" link read it from here.
                  setPageAccent(latestSettings.primaryColor)

                  // Update price tag icon color for all types
                  document
                    .querySelectorAll('.course-container-header-price svg path')
                    .forEach(path => {
                      path.setAttribute('fill', latestSettings.primaryColor)
                    })

                  // Update promo check icons
                  Array.from(
                    document.getElementsByClassName('promo-check-icon'),
                  ).forEach(element => {
                    element.style.backgroundColor = latestSettings.primaryColor
                  })

                  // Update logo based on landing page type
                  const logoUrl = latestSettings.logo
                  if (logoUrl) {
                    const timestamp = new Date().getTime()
                    const fullImageUrl = logoUrl.startsWith('http')
                      ? `${logoUrl}?t=${timestamp}`
                      : `https://cdn.minicoursegenerator.com/${logoUrl}?t=${timestamp}`

                    // Define logo selectors based on type
                    const logoSelectors = {
                      1: {
                        container: '.editor-header-img-container',
                        logo: '.header-logo',
                      },
                      2: {
                        container: '.hero-logo-container',
                        logo: '.hero-logo',
                      },
                      3: {
                        container: '.promo-logo-container',
                        logo: '.promo-logo',
                      },
                    }

                    const selectors =
                      logoSelectors[currentLandingPageType] || logoSelectors[1]

                    // Find the container element
                    const logoContainer = document.querySelector(
                      selectors.container,
                    )

                    if (logoContainer) {
                      // Look for existing logo
                      let logoImage = logoContainer.querySelector(
                        selectors.logo,
                      )

                      // If logo exists, update it
                      if (logoImage) {
                        logoImage.src = fullImageUrl
                      } else {
                        // Create new image element if it doesn't exist
                        logoImage = document.createElement('img')
                        logoImage.src = fullImageUrl
                        logoImage.className = selectors.logo.substring(1) // Remove the dot
                        logoImage.alt = 'logo'
                        logoContainer.appendChild(logoImage)
                      }
                    } else {
                      // Container doesn't exist, create it based on type
                      if (currentLandingPageType === 1) {
                        // For type 1 (DEFAULT), create container if it doesn't exist
                        const editorHeader = document.querySelector(
                          '.editor-header',
                        )

                        if (editorHeader) {
                          const newHeaderContainer = document.createElement(
                            'div',
                          )
                          newHeaderContainer.className =
                            'editor-header-img-container'

                          const headerImage = document.createElement('img')
                          headerImage.src = fullImageUrl
                          headerImage.className = 'header-logo'
                          headerImage.alt = 'logo'

                          newHeaderContainer.appendChild(headerImage)
                          editorHeader.appendChild(newHeaderContainer)
                        }
                      } else if (currentLandingPageType === 2) {
                        // For type 2 (HERO), create container in hero-content-section
                        const heroContentSection = document.querySelector(
                          '.hero-content-section',
                        )

                        if (heroContentSection) {
                          const newLogoContainer = document.createElement('div')
                          newLogoContainer.className = 'hero-logo-container'

                          const logoImage = document.createElement('img')
                          logoImage.src = fullImageUrl
                          logoImage.className = 'hero-logo'
                          logoImage.alt = 'logo'

                          newLogoContainer.appendChild(logoImage)
                          // Insert at the beginning of hero-content-section
                          heroContentSection.insertBefore(
                            newLogoContainer,
                            heroContentSection.firstChild,
                          )
                        }
                      } else if (currentLandingPageType === 3) {
                        // For type 3 (PROMO), create container in promo-image-placeholder
                        const promoImagePlaceholder = document.querySelector(
                          '.promo-image-placeholder',
                        )

                        if (promoImagePlaceholder) {
                          const newLogoContainer = document.createElement('div')
                          newLogoContainer.className = 'promo-logo-container'

                          const logoImage = document.createElement('img')
                          logoImage.src = fullImageUrl
                          logoImage.className = 'promo-logo'
                          logoImage.alt = 'logo'

                          newLogoContainer.appendChild(logoImage)
                          // Insert at the beginning of promo-image-placeholder
                          promoImagePlaceholder.insertBefore(
                            newLogoContainer,
                            promoImagePlaceholder.firstChild,
                          )
                        }
                      }
                    }
                  } else {
                    // Remove logo if logoUrl is empty
                    const logoSelectors = {
                      1: {
                        container: '.editor-header-img-container',
                        logo: '.header-logo',
                      },
                      2: {
                        container: '.hero-logo-container',
                        logo: '.hero-logo',
                      },
                      3: {
                        container: '.promo-logo-container',
                        logo: '.promo-logo',
                      },
                    }

                    const selectors =
                      logoSelectors[currentLandingPageType] || logoSelectors[1]
                    const logoContainer = document.querySelector(
                      selectors.container,
                    )

                    if (logoContainer) {
                      const logoImage = logoContainer.querySelector(
                        selectors.logo,
                      )
                      if (logoImage) {
                        logoImage.remove()
                      }
                    }
                  }
                }

                dispatch(messageBoxHideAction())
                if (pageUrl === 'mini-course') {
                  dispatch(courseGetAction(id))
                }
              },
            ),
          ],
          component: (
            <LandingPageSettings
              onSave={settings => {
                // This callback will be called whenever settings change
                latestSettings = settings
              }}
            />
          ),
          maxWidth: '960px',
          maxHeight: '80vh',
        }),
      ),
    )
  }

  const navigateAway = () => {
    if (portalPage?.returnTo) {
      history.push(portalPage.returnTo)
    } else if (pageUrl === 'mini-course') {
      history.push('/mini-course/' + id + '/landing-page')
    } else {
      history.push('/collection/' + id + '/landing-page')
    }
    window.scrollTo(0, 0)
  }

  const closeEditor = () => {
    const hasUnsavedChanges = editorChanges.hasChanges()

    if (hasUnsavedChanges) {
      // Show confirmation modal
      dispatch(
        messageBoxShowAction(
          MessageBoxContent({
            title: `<div style="display: flex; align-items: center; justify-content: center; gap: 8px; color: var(--mcg-dark, #002E47); font-family: Inter; font-size: 18px; font-weight: 600; line-height: 28px;">
              <Typography>Unsaved Changes</Typography>
            </div>`,
            type: MessageBoxType.WARNING,
            message:
              'You have unsaved changes. Do you want to leave without saving?',
            buttons: [
              new ButtonData(
                ButtonType.NORMAL,
                'Discard changes',
                ButtonActionType.NO,
                () => {
                  dispatch(messageBoxHideAction())
                  navigateAway()
                },
              ),
              new ButtonData(
                ButtonType.ACTION,
                'Save and close editor',
                ButtonActionType.YES,
                () => {
                  handleClick() // Save changes
                  dispatch(messageBoxHideAction())
                },
              ),
            ],
            component: (
              <div
                style={{
                  gap: '8px',
                  color: 'var(--mcg-dark, #002E47)',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '22px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <p className={classes.title}>
                  You have unsaved changes. If you leave the editor now, your
                  changes will be lost.
                </p>
                <p className={classes.closeTitle}>
                  What would you like to do?{' '}
                </p>
              </div>
            ),
            maxWidth: '500px',
          }),
        ),
      )
    } else {
      // No changes, just navigate away
      navigateAway()
    }
  }

  return (
    <div className={classes.content}>
      <div className={classes.header} ref={headerRef}>
        <div className={classes.closeButton}>
          <CoreButton
            startIcon={<Icon icon="eva:close-outline" />}
            color="secondary"
            variant="text"
            size="small"
            onClick={closeEditor}
          >
            Close editor
          </CoreButton>
          {portalPage && (
            <>
              <div
                style={{ width: 1, height: 24, backgroundColor: '#E5E7EB' }}
              />
              <div
                style={{
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 10,
                }}
              >
                <PortalPageName
                  name={portalPage.name}
                  onRename={renamePortalPage}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'ui-monospace, Menlo, monospace',
                    fontSize: 12,
                    color: '#919EAB',
                  }}
                >
                  {portalHost}
                  {portalPage.path === '/' ? '' : portalPage.path}
                </span>
              </div>
            </>
          )}
        </div>
        <div className={classes.buttonDiv}>
          <CoreButton
            onClick={handleEditorPaste}
            size="small"
            color="secondary"
            variant="text"
            disabled={!clipboardHasEditorContent}
          >
            Paste
          </CoreButton>
          <CoreButton
            onClick={handleEditorCopy}
            size="small"
            color="secondary"
            variant="text"
          >
            Copy content
          </CoreButton>
          <div style={{ width: 1, height: 24, backgroundColor: '#E5E7EB' }} />
          <CoreButton
            onClick={handleSettings}
            size="small"
            color="secondary"
            variant="text"
          >
            Settings
          </CoreButton>
          <CoreButton
            onClick={() => handleClick()}
            size="small"
            color="secondary"
            variant="contained"
          >
            Save changes
          </CoreButton>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(CoverHeader)
